import { useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { formatDate } from '@/lib/utils/date';
import {
  useAdminHistoricalCampaigns,
  useCampaignReportDownload,
  useFetchVoiceBase,
} from '../api';
import { buildCampaignColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import type { CampaignRow } from '../types';

export function AdminHistoricalCampaignsPage() {
  const userId = useAuthStore((s) => s.userId);
  // SADMIN / sub-super-admin see the Parent Username column; resellers don't.
  const showParentUsername = useAuthStore((s) => s.isSubOrSuperAdmin());
  const search = useAdminHistoricalCampaigns();
  const rows = search.data ?? [];
  const fetchBase = useFetchVoiceBase();

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [username, setUsername] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const handleViewBase = (row: CampaignRow) => {
    fetchBase.mutate(row.campaignId, {
      onSuccess: (res) => {
        const url = Array.isArray(res) ? res[0]?.baseUrl : undefined;
        if (!url) {
          toast.error('Base file URL not found.');
          return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      },
      onError: () => toast.error('Error opening base file.'),
    });
  };

  const reportDownload = useCampaignReportDownload();
  const columns = useMemo(
    () =>
      buildCampaignColumns({
        showOwner: true,
        showParentUsername,
        onViewBase: showParentUsername ? handleViewBase : undefined,
        onDownloadReport: reportDownload.download,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showParentUsername, reportDownload.download],
  );

  const visibility = {
    ...DEFAULT_VISIBLE_COLUMNS,
    userId: true,
    username: true,
    parentUsername: showParentUsername,
  };

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setHasSearched(true);
    search.mutate(
      {
        userId: Number(userId),
        startDate: startDate ? formatDate(startDate) : '',
        endDate: endDate ? formatDate(endDate) : '',
        username: username.trim() || undefined,
      },
      {
        onError: () => toast.error('Could not fetch historical campaigns.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Campaign Archive"
        description="Look up completed campaigns across users by date and optional username."
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>
            Leave dates empty to fetch all snapshots; username narrows to a single owner.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start date</Label>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                placeholder="Pick a start date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-date">End date</Label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                placeholder="Pick an end date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="username">Username (optional)</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Filter by username"
              />
            </div>
            <Button onClick={handleSearch} disabled={search.isPending}>
              {search.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<CampaignRow, unknown>
        tableId="campaigns:voice:admin-historical:v1"
        columns={columns}
        data={rows}
        isLoading={search.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle={hasSearched ? 'No campaigns match' : 'Pick a range to begin'}
        emptyDescription={
          hasSearched
            ? 'Try widening the date range or removing the username filter.'
            : 'Choose start / end dates above and click Search.'
        }
        getRowId={(row) => String(row.campaignId)}
        initialColumnVisibility={visibility}
        exportFilename="voice-campaigns-admin-historical.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
